import React, { useEffect, useState } from 'react';
import {
  Button, Table, Switch, Popconfirm, message, Tag, Dropdown, Input,
} from 'antd';
import {
  EditOutlined, DeleteOutlined, PlusOutlined, EllipsisOutlined,
  DollarOutlined, BarChartOutlined, SearchOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPut, httpDelete } from '../../store/http';
import { hideLoader, showLoader } from '../../utils/loader';
import Swal from 'sweetalert2';
import CreateProductModal from './CreateProductModal';
import EditProductModal from './EditProductModal';
import { useNavigate } from 'react-router-dom';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import './ProductList.css';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [selectedProductToEdit, setSelectedProductToEdit] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const navigate = useNavigate();

  const fetchProducts = async (page = 1, size = 10) => {
    setLoading(true);
    showLoader();
    try {
      const response = await httpGet(`${customBaseUrl.integUrl}/api/v1/products?page=${page - 1}&size=${size}`);
      if (response.status) {
        setProducts(response.data.content);
        setFilteredProducts(response.data.content);
        setPagination({
          ...pagination,
          current: page,
          pageSize: size,
          total: response.data.totalElements,
        });
      } else {
        Swal.fire('Error', response.message || 'Failed to fetch products', 'error');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      Swal.fire('Error', 'An error occurred while fetching products', 'error');
    } finally {
      hideLoader();
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (pag) => {
    fetchProducts(pag.current, pag.pageSize);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const filtered = products.filter((p) =>
      p.productName?.toLowerCase().includes(value.toLowerCase()) ||
      p.productCode?.toLowerCase().includes(value.toLowerCase()) ||
      p.description?.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const handleToggleActive = async (productCode, isActive, productName, description, status) => {
    showLoader();
    try {
      const response = await httpPut(`${customBaseUrl.integUrl}/api/v1/products/${productCode}`, {
        isActive: !isActive, productName, description, status,
      });
      if (response.status) {
        message.success(`Product ${isActive ? 'deactivated' : 'activated'} successfully`);
        fetchProducts(pagination.current, pagination.pageSize);
      } else {
        Swal.fire('Error', response.message || 'Failed to update product status', 'error');
      }
    } catch (error) {
      console.error('Error toggling product status:', error);
      Swal.fire('Error', 'An error occurred while updating product status', 'error');
    } finally {
      hideLoader();
    }
  };

  const handleDeleteProduct = async (productCode) => {
    showLoader();
    try {
      const response = await httpDelete(`${customBaseUrl.integUrl}/api/v1/products/${productCode}`);
      if (response.status) {
        message.success('Product deleted successfully');
        fetchProducts(pagination.current, pagination.pageSize);
      } else {
        Swal.fire('Error', response.message || 'Failed to delete product', 'error');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      Swal.fire('Error', 'An error occurred while deleting product', 'error');
    } finally {
      hideLoader();
    }
  };

  const handleEditProduct = (record) => {
    setSelectedProductToEdit(record);
    setShowEditProductModal(true);
  };

  const handleManagePricing = (record) => {
    navigate(`/b2b-products/${record.productCode}/pricing`);
  };

  const handleViewTransactions = (record) => {
    navigate(`/b2b-products/${record.productCode}/transactions`, {
      state: { productName: record.productName, productCode: record.productCode },
    });
  };

  const getActionMenuItems = (record) => [
    {
      key: 'edit',
      label: 'Edit Product',
      icon: <EditOutlined />,
      onClick: () => handleEditProduct(record),
    },
    {
      key: 'pricing',
      label: 'Manage Pricing',
      icon: <DollarOutlined />,
      onClick: () => handleManagePricing(record),
    },
    {
      key: 'transactions',
      label: 'View Transactions',
      icon: <BarChartOutlined />,
      onClick: () => handleViewTransactions(record),
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: (
        <Popconfirm
          title="Are you sure you want to delete this product?"
          onConfirm={() => handleDeleteProduct(record.productCode)}
          okText="Yes, Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <span style={{ color: '#ff4d4f' }}>
            <DeleteOutlined style={{ marginRight: 8 }} />
            Delete Product
          </span>
        </Popconfirm>
      ),
      danger: false,
    },
  ];

  const columns = [
    {
      title: 'Product Name',
      dataIndex: 'productName',
      key: 'productName',
      render: (name) => (
        <span style={{ fontWeight: 600, color: '#1a1a1a' }}>{name}</span>
      ),
    },
    {
      title: 'Product Code',
      dataIndex: 'productCode',
      key: 'productCode',
      render: (code) => (
        <Tag
          style={{
            fontFamily: 'monospace',
            background: '#fff3eb',
            color: '#ff6700',
            border: '1px solid #ffbb96',
            borderRadius: 6,
            fontWeight: 600,
          }}
        >
          {code}
        </Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc) => (
        <span style={{ color: '#595959' }}>{desc || '—'}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const s = (status || '').toLowerCase();
        const config = {
          active: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', label: 'ACTIVE' },
          inactive: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffa39e', label: 'INACTIVE' },
          pending: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', label: 'PENDING' },
        };
        const cfg = config[s] || { color: '#8c8c8c', bg: '#f5f5f5', border: '#d9d9d9', label: status || '—' };
        return (
          <Tag
            style={{
              color: cfg.color,
              background: cfg.bg,
              border: `1px solid ${cfg.border}`,
              borderRadius: 20,
              fontWeight: 600,
              fontSize: 11,
              padding: '2px 10px',
            }}
          >
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      render: (active, record) => (
        <Switch
          checked={record.active}
          onChange={() =>
            handleToggleActive(record.productCode, record.active, record.productName, record.description, record.status)
          }
          checkedChildren="ON"
          unCheckedChildren="OFF"
          style={{ minWidth: 60 }}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<EllipsisOutlined style={{ fontSize: 20, fontWeight: 700 }} />}
            className="product-action-btn"
          />
        </Dropdown>
      ),
    },
  ];

  const activeCount = filteredProducts.filter((p) => p.active).length;
  const inactiveCount = filteredProducts.filter((p) => !p.active).length;

  return (
    <div className="product-list-wrapper">
      {/* ── Header Card ───────────────────────────────── */}
      <PageHeadingBanner
        icon={<AppstoreOutlined />}
        title="B2B Products"
        subtitle="Manage your product catalogue, pricing and transactions"
        actions={
          <Button
            icon={<PlusOutlined />}
            onClick={() => setShowCreateProductModal(true)}
            className="phb-btn-outline"
            size="large"
          >
            Create Product
          </Button>
        }
      />

      {/* ── Stats Row ─────────────────────────────────── */}
      <div className="product-stats-row">
        <div className="product-stat-card">
          <span className="product-stat-value">{pagination.total || filteredProducts.length}</span>
          <span className="product-stat-label">Total Products</span>
        </div>
        <div className="product-stat-card stat-active">
          <span className="product-stat-value" style={{ color: '#52c41a' }}>{activeCount}</span>
          <span className="product-stat-label">Active</span>
        </div>
        <div className="product-stat-card stat-inactive">
          <span className="product-stat-value" style={{ color: '#ff4d4f' }}>{inactiveCount}</span>
          <span className="product-stat-label">Inactive</span>
        </div>
      </div>

      {/* ── Search Bar ────────────────────────────────── */}
      <div className="product-search-bar">
        <Input
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          placeholder="Search by name, code or description…"
          allowClear
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          className="product-search-input"
        />
      </div>

      {/* ── Table ─────────────────────────────────────── */}
      <div className="product-table-card">
        <Table
          columns={columns}
          dataSource={filteredProducts}
          rowKey="productCode"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} products`,
          }}
          onChange={handleTableChange}
          rowClassName={(_, idx) => (idx % 2 === 0 ? 'product-row-even' : 'product-row-odd')}
          className="product-table"
        />
      </div>

      <CreateProductModal
        showModal={showCreateProductModal}
        hideModal={() => setShowCreateProductModal(false)}
        reloadProducts={fetchProducts}
      />
      <EditProductModal
        showModal={showEditProductModal}
        hideModal={() => setShowEditProductModal(false)}
        reloadProducts={fetchProducts}
        product={selectedProductToEdit}
      />
    </div>
  );
};

export default ProductList;
